class AlternateCarouselComponent extends HTMLElement {
  /** @type {boolean} */ #isDown = false;
  /** @type {number} */ #startX = 0;
  /** @type {number} */ #scrollLeft = 0;
  /** @type {boolean} */ #moved = false;
  /** @type {number} */ #lastX = 0;
  /** @type {number} */ #lastTime = 0;
  /** @type {number} */ #velocity = 0;
  /** @type {number|null} */ #rafId = null;
  /** @type {boolean} */ #autoplay = false;
  /** @type {HTMLElement|null} */ #track = null;
  /** @type {number|null} */ #resumeTimeoutId = null;

  connectedCallback() {
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('click', this.#onClick, true);
    this.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('scrollend', this.#onScrollEnd, { passive: true });

    this.#track = this.querySelector('.alternate-carousel__track');

    if (this.dataset.autoplay !== undefined && this.#track) {
      this.#autoplay = true;
      this.#initAutoplay(this.#track);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('scrollend', this.#onScrollEnd);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#resumeTimeoutId !== null) clearTimeout(this.#resumeTimeoutId);
  }

  /**
   * Autoplay is a CSS `transform` animation (like the marquee component), not JS-driven
   * `scrollLeft` writes — some browsers can silently fail to apply per-frame `scrollLeft`/`scrollTo`
   * writes on certain scroll containers, while a compositor-driven CSS animation is reliable everywhere.
   * @param {HTMLElement} track
   */
  #initAutoplay(track) {
    const originals = Array.from(track.children);
    originals.forEach((child) => {
      const clone = /** @type {HTMLElement} */ (child.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    // Reading scrollWidth forces layout synchronously, so the duration is correct before the
    // animation's first paint — no flash of the CSS fallback duration.
    const pxPerSecond = 30;
    const contentWidth = track.scrollWidth / 2;
    const duration = Math.max(contentWidth / pxPerSecond, 1);
    this.style.setProperty('--ac-duration', `${duration}s`);
  }

  #pauseAutoplay() {
    this.#track?.getAnimations().forEach((animation) => animation.pause());
  }

  #resumeAutoplay() {
    this.#track?.getAnimations().forEach((animation) => animation.play());
  }

  #isAutoplayRunning() {
    return this.#track?.getAnimations().some((animation) => animation.playState === 'running') ?? false;
  }

  /**
   * Detects user-driven scrolling (e.g. touch drag/momentum) so autoplay never fights it.
   * `scrollend` (below) is the authoritative resume signal; this debounce is only a fallback
   * for browsers without `scrollend` support, since some browsers (notably iOS Safari) dispatch
   * `scroll` very sparsely during momentum, so a short debounce there can fire mid-momentum.
   */
  #onScroll = () => {
    if (!this.#autoplay || this.#isAutoplayRunning()) return;
    if (this.#resumeTimeoutId !== null) clearTimeout(this.#resumeTimeoutId);
    this.#resumeTimeoutId = setTimeout(() => {
      this.#resumeTimeoutId = null;
      if (!this.#isDown) this.#resumeAutoplay();
    }, 800);
  };

  /** Fires once when scrolling (including native momentum) has fully settled; resumes autoplay immediately */
  #onScrollEnd = () => {
    if (!this.#autoplay || this.#isAutoplayRunning()) return;
    if (this.#resumeTimeoutId !== null) {
      clearTimeout(this.#resumeTimeoutId);
      this.#resumeTimeoutId = null;
    }
    if (!this.#isDown) this.#resumeAutoplay();
  };

  #onPointerDown = (/** @type {PointerEvent} */ e) => {
    if (this.#autoplay) this.#pauseAutoplay();
    if (e.pointerType === 'touch') return;
    e.preventDefault();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#isDown = true;
    this.#moved = false;
    this.#startX = e.clientX;
    this.#lastX = e.clientX;
    this.#lastTime = e.timeStamp;
    this.#velocity = 0;
    this.#scrollLeft = this.scrollLeft;
    this.setPointerCapture(e.pointerId);
    this.style.cursor = 'grabbing';
  };

  #onPointerMove = (/** @type {PointerEvent} */ e) => {
    if (!this.#isDown) return;
    const delta = e.clientX - this.#startX;
    if (Math.abs(delta) > 5) this.#moved = true;
    this.scrollLeft = this.#scrollLeft - delta;

    const dt = e.timeStamp - this.#lastTime;
    if (dt > 0) this.#velocity = (e.clientX - this.#lastX) / dt;
    this.#lastX = e.clientX;
    this.#lastTime = e.timeStamp;
  };

  #onPointerUp = () => {
    if (!this.#isDown) return;
    this.#isDown = false;
    this.style.cursor = '';
    if (this.#autoplay) {
      this.#resumeAutoplay();
    } else {
      this.#startMomentum();
    }
  };

  #startMomentum = () => {
    /** @type {number|null} */ let lastTime = null;

    const tick = (/** @type {number} */ now) => {
      if (lastTime !== null) {
        const dt = now - lastTime;
        // Decay ~92% per 16ms frame, normalised for actual frame time
        this.#velocity *= Math.pow(0.92, dt / 16);
        if (Math.abs(this.#velocity) < 0.05) {
          this.#rafId = null;
          return;
        }
        this.scrollLeft -= this.#velocity * dt;
      }
      lastTime = now;
      this.#rafId = requestAnimationFrame(tick);
    };

    this.#rafId = requestAnimationFrame(tick);
  };

  /** Prevent link/click from firing after a drag */
  #onClick = (/** @type {MouseEvent} */ e) => {
    if (this.#moved) {
      e.preventDefault();
      e.stopPropagation();
      this.#moved = false;
    }
  };
}

customElements.define('alternate-carousel-component', AlternateCarouselComponent);
